import type {
  BackendEffectiveStopDto,
  BackendLimitChangeDto,
  BackendLimitsDto,
  BackendPolicyCheckDto,
  BackendPolicyDecisionDto,
  BackendStopDto,
  WithdrawalApprovalDto,
  WithdrawalApprovalStatus,
  WithdrawalLimitChangeDto,
  WithdrawalLimitProfileDto,
  WithdrawalLimitsDto,
  WithdrawalPolicyCheckDto,
  WithdrawalPolicyDecisionDto,
  WithdrawalPolicyOutcome,
  WithdrawalStopDto,
  WithdrawalStopScope,
} from '../contracts/withdrawal-policy';
import type { WithdrawalUserDto } from '../contracts';

const POLICY_OUTCOME: Record<string, WithdrawalPolicyOutcome> = {
  ALLOW: 'passed',
  DENY: 'denied',
  PENDING: 'pending',
  HOLD: 'pending',
  passed: 'passed',
  denied: 'denied',
  pending: 'pending',
};

const APPROVAL_STATUS: Record<string, WithdrawalApprovalStatus> = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REVOKED: 'REVOKED',
  NA: 'NOT_REQUIRED',
  NOT_REQUIRED: 'NOT_REQUIRED',
};

const asList = <T,>(value: T[] | { data?: T[] } | null | undefined): T[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : value.data ?? [];
};

const utcParts = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return { year: date.getUTCFullYear(), month: date.getUTCMonth(), day: date.getUTCDate() };
};

const sameUtcDay = (value: string, now: Date) => {
  const parts = utcParts(value);
  return parts?.year === now.getUTCFullYear() && parts.month === now.getUTCMonth() && parts.day === now.getUTCDate();
};

const sameUtcMonth = (value: string, now: Date) => {
  const parts = utcParts(value);
  return parts?.year === now.getUTCFullYear() && parts.month === now.getUTCMonth() && parts.day === 1;
};

export const parseLimitScope = (
  scopeKey: string,
): Pick<WithdrawalLimitProfileDto, 'scope' | 'assetCode' | 'networkCode'> | null => {
  if (scopeKey === 'ALL') return { scope: 'ALL' };
  const [head, first, second] = scopeKey.split(':');
  if (head === 'ASSET' && first && !second) return { scope: 'ASSET', assetCode: first };
  if (head === 'NETWORK' && first && !second) return { scope: 'NETWORK', networkCode: first };
  if (head === 'ASSET_NETWORK' && first && second) return { scope: 'ASSET_NETWORK', assetCode: first, networkCode: second };
  return null;
};

export const parseStopScope = (scopeKey: string): { scope: WithdrawalStopScope; targetId?: string } | null => {
  if (scopeKey === 'GLOBAL') return { scope: 'GLOBAL' };
  const [head, target, extra] = scopeKey.split(':');
  if ((head === 'USER' || head === 'ASSET' || head === 'NETWORK') && target && !extra) {
    return { scope: head, targetId: target };
  }
  return null;
};

const usedFor = (
  usage: BackendLimitsDto['usage'],
  profileId: string | undefined,
  period: 'DAY' | 'MONTH',
  now: Date,
) => {
  if (!profileId) return null;
  const row = (usage ?? []).find((item) => {
    if (item.profileId !== profileId || item.period !== period) return false;
    return period === 'DAY' ? sameUtcDay(item.periodStart, now) : sameUtcMonth(item.periodStart, now);
  });
  return row ? String(row.usedUsdt) : null;
};

export const mapWithdrawalLimits = (body: BackendLimitsDto | null | undefined, now = new Date()): WithdrawalLimitsDto => {
  const profiles: WithdrawalLimitProfileDto[] = [];
  for (const item of body?.items ?? []) {
    if (item.deletedAt) continue;
    const parsed = parseLimitScope(item.scopeKey);
    if (!parsed) continue;
    profiles.push({
      id: item.id,
      ...parsed,
      version: item.version,
      singleUsdt: String(item.singleUsdt),
      dailyUsdt: String(item.dailyUsdt),
      monthlyUsdt: String(item.monthlyUsdt),
      dailyUsedUsdt: usedFor(body?.usage, item.id, 'DAY', now),
      monthlyUsedUsdt: usedFor(body?.usage, item.id, 'MONTH', now),
    });
  }
  return { timezone: body?.timezone ?? 'UTC', profiles };
};

export const mapLimitChanges = (
  body: BackendLimitChangeDto[] | { data?: BackendLimitChangeDto[] } | null | undefined,
): WithdrawalLimitChangeDto[] =>
  asList(body).flatMap((item) => {
    const parsed = parseLimitScope(item.scopeKey);
    const action = item.command?.action;
    if (!parsed || (action !== 'UPSERT' && action !== 'DELETE')) return [];
    return [{
      id: item.id,
      merchantId: String(item.userId),
      scope: parsed.scope,
      assetCode: parsed.assetCode ?? null,
      networkCode: parsed.networkCode ?? null,
      action,
      version: item.baseVersion,
      authorId: String(item.authorUserId),
      createdAt: item.createdAt,
      status: item.state,
      singleUsdt: item.command.singleUsdt ?? null,
      dailyUsdt: item.command.dailyUsdt ?? null,
      monthlyUsdt: item.command.monthlyUsdt ?? null,
    }];
  });

export const mapStops = (
  body: BackendStopDto[] | { data?: BackendStopDto[] } | null | undefined,
): WithdrawalStopDto[] =>
  asList(body).map((item) => {
    const parsed = parseStopScope(item.scopeKey);
    return {
      id: item.id,
      scope: parsed?.scope,
      targetId: parsed?.targetId ?? null,
      reason: item.reason,
      active: item.enabled !== false,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      scopeKey: item.scopeKey,
    };
  });

const mapCheck = (check: BackendPolicyCheckDto): WithdrawalPolicyCheckDto => ({
  name: check.rule,
  result: check.status,
});

export const mapPolicyDecisions = (
  body: BackendPolicyDecisionDto[] | { data?: BackendPolicyDecisionDto[] } | null | undefined,
): WithdrawalPolicyDecisionDto[] =>
  asList(body).map((item) => ({
    id: item.id,
    phase: item.phase,
    outcome: POLICY_OUTCOME[item.outcome] ?? item.outcome,
    createdAt: item.createdAt,
    checks: (item.checks ?? []).map(mapCheck),
  }));

const coolingActiveFrom = (checks: BackendPolicyCheckDto[] | undefined): string | undefined => {
  const cooling = checks?.find((item) => item.rule === 'COOLING_PERIOD' && item.status === 'PENDING');
  const details = cooling?.details;
  if (!details || typeof details !== 'object' || !('activeFrom' in details)) return undefined;
  const activeFrom = (details as { activeFrom?: unknown }).activeFrom;
  return typeof activeFrom === 'string' ? activeFrom : undefined;
};

const isBackendStop = (
  stop: WithdrawalStopDto | BackendEffectiveStopDto,
): stop is BackendEffectiveStopDto => 'scopeKey' in stop && typeof stop.scopeKey === 'string' && !('scope' in stop);

export const mapWithdrawalPolicyFields = (dto: WithdrawalUserDto) => {
  const checks = dto.checks ?? [];
  const policyChecks = checks.length > 0 ? checks.map(mapCheck) : dto.policyChecks;
  const outcome = dto.policyOutcome ? POLICY_OUTCOME[dto.policyOutcome] : undefined;
  const approvalStatus = dto.approvalState ? APPROVAL_STATUS[dto.approvalState] : undefined;
  const approval: WithdrawalApprovalDto | undefined = dto.approval
    ?? (approvalStatus ? { status: approvalStatus } : undefined);
  const activeFrom = dto.addressActiveFrom ?? coolingActiveFrom(checks);
  const addressHold = dto.addressHold === true || checks.some((item) => item.rule === 'COOLING_PERIOD' && item.status === 'PENDING');
  const effectiveStops = (dto.effectiveStops ?? []).map((stop) => {
    if (!isBackendStop(stop)) return stop;
    const parsed = parseStopScope(stop.scopeKey);
    return {
      id: stop.id,
      scope: parsed?.scope,
      targetId: parsed?.targetId ?? null,
      active: true,
      scopeKey: stop.scopeKey,
    } satisfies WithdrawalStopDto;
  });

  return {
    amountUsdt: dto.amountUsdt != null && dto.amountUsdt !== '' ? String(dto.amountUsdt) : undefined,
    addressHold,
    addressActiveFrom: activeFrom,
    approval,
    policyOutcome: outcome,
    policyChecks,
    effectiveStops: effectiveStops.length > 0 ? effectiveStops : undefined,
  };
};
